import { createElement, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { StaleEditBanner } from './StaleEditBanner';
import { staleFieldProps, type StaleFieldOptions } from './staleFieldProps';
import { useStaleEdit, type StaleConflict } from './useStaleEdit';

/** What a screen's own read returns for the record, in the form's shape. */
export interface VersionedSnapshot<T> {
  values: T;
  version: number;
}

export interface UseVersionedFormOptions<T> {
  /** The fields the three-way merge compares, as module-level constant. */
  fields: readonly (keyof T & string)[];
  /** How each field reads in the banner ("End date"); defaults to the field name. */
  labels?: Partial<Record<keyof T & string, string>>;
}

/**
 * Option C's form plumbing for a record edited in a dialog or a page form
 * (docs/architecture/concurrent-edits.md): the values the form holds, the base it loaded, the version it
 * sends, and the conflict a refused save leaves behind.
 *
 * Lifecycle: `load` when the form opens (or a record is picked), `recover`
 * when a save throws, `saved` when it succeeds, `clear` when it closes. The
 * merge itself is {@link useStaleEdit}; this hook only keeps the three pieces
 * of state a screen would otherwise each re-invent.
 */
export function useVersionedForm<T extends Record<string, unknown>>({
  fields,
  labels,
}: UseVersionedFormOptions<T>) {
  const [values, setValues] = useState<T | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const base = useRef<T | null>(null);
  const { conflict, clashes, resolve, settle, clear } = useStaleEdit<T>(fields);

  const load = useCallback(
    (loaded: T, loadedVersion: number | null | undefined) => {
      base.current = loaded;
      setValues(loaded);
      setVersion(loadedVersion ?? null);
      clear();
    },
    [clear],
  );

  /**
   * Sets some fields on the form and its base together, for a change that
   * arrived from the server rather than from the reader (a sibling panel's
   * save that recomputes two of this form's fields). The form stays clean for
   * those fields, so a later stale refusal does not count them as the reader's.
   */
  const patch = useCallback((fieldsToSet: Partial<T>, nextVersion?: number | null) => {
    if (base.current) base.current = { ...base.current, ...fieldsToSet };
    setValues((held) => (held ? { ...held, ...fieldsToSet } : held));
    if (nextVersion != null) setVersion(nextVersion);
  }, []);

  /** After a successful save the form's values are the new base; `next` is the response's version. */
  const saved = useCallback(
    (next?: number | null) => {
      base.current = values;
      if (next != null) setVersion(next);
      clear();
    },
    [values, clear],
  );

  /**
   * Handles a failed save. True when it was a stale refusal and the form now
   * holds the merge (the caller shows no error of its own); false for any other
   * error, which the caller reports as it always did. A failed re-read counts as
   * "not handled", so the refusal's own message is the one shown.
   */
  const recover = useCallback(
    async (error: unknown, reload: () => Promise<VersionedSnapshot<T>>): Promise<boolean> => {
      if (!values || !base.current) return false;
      let latest: number | null = null;
      try {
        const resolution = await resolve(error, {
          base: base.current,
          mine: values,
          reload: async () => {
            const snapshot = await reload();
            latest = snapshot.version;
            return snapshot.values;
          },
        });
        if (!resolution) return false;
        base.current = resolution.theirs;
        setValues(resolution.merged);
        setVersion(latest);
        return true;
      } catch {
        return false;
      }
    },
    [values, resolve],
  );

  const takeTheirs = useCallback(
    (field: keyof T & string, current: StaleConflict<T>) => {
      setValues((held) => (held ? { ...held, [field]: current.theirs[field] } : held));
      settle(field);
    },
    [settle],
  );

  /** "Discard mine": the other version wholesale, and the conflict is over. */
  const discardMine = useCallback(() => {
    if (!conflict) return;
    base.current = conflict.theirs;
    setValues(conflict.theirs);
    clear();
  }, [conflict, clear]);

  const banner: ReactNode = useMemo(() => {
    if (!conflict) return null;
    return createElement(StaleEditBanner, {
      who: conflict.who,
      clashLabels: clashes.map((f) => labels?.[f] ?? f),
      onDiscardMine: discardMine,
      compact: true,
    });
  }, [conflict, clashes, labels, discardMine]);

  /** Props for a field: the note by who changed it, and "Use …'s" on a clash. */
  const field = useCallback(
    <K extends keyof T & string>(name: K, options: Omit<StaleFieldOptions<T[K]>, 'onUseTheirs'> = {}) => {
      return staleFieldProps<T, K>(conflict, name, {
        label: labels?.[name],
        ...options,
        onUseTheirs: conflict ? () => takeTheirs(name, conflict) : undefined,
      });
    },
    [conflict, takeTheirs, labels],
  );

  return {
    values,
    setValues,
    version,
    setVersion,
    /** The values as loaded, for dirty checks. */
    base: base.current,
    conflict,
    banner,
    field,
    /** The primary button's label: "Save again" while a conflict is open. */
    saveLabel: (label: string) => (conflict ? 'Save again' : label),
    load,
    patch,
    saved,
    recover,
    clear,
    discardMine,
  };
}

export type VersionedForm<T extends Record<string, unknown>> = ReturnType<typeof useVersionedForm<T>>;

/**
 * What a presentational dialog needs from {@link useVersionedForm}: the
 * banner, a field-props factory and the primary label. Optional on a dialog so
 * it still renders (and tests) without a conflict.
 */
export interface StaleFormBinding<T extends Record<string, unknown>> {
  banner: ReactNode;
  field: VersionedForm<T>['field'];
  saveLabel: (label: string) => string;
}
